// Refactored sidebar chat following official Convex agent patterns
// Based on: https://github.com/get-convex/agent/blob/main/example/ui/chat/ChatStreaming.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery, useAction } from "convex/react";
import { useUser } from "@clerk/clerk-react";
import { api } from "../../../../../convex/_generated/api";
import { useOrganizationSync } from "@/hooks/useOrganizationSync";
import {
  MessageSquare,
  Send,
  Bot,
  User,
  Loader2,
  X,
  AlertCircle,
  Plus,
  History,
  ArrowLeft,
  Trash2,
  Square,
  Paperclip,
  HardDrive,
  Book,
  Image as ImageIcon,
  PenSquare,
  MoreHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAgentSidebar } from "@/contexts/AgentSidebarContext";
import { useAgentCanvas } from "@/contexts/AgentCanvasContext";
import { ChatErrorBoundary } from "../chat/ChatErrorBoundary";
import { StreamingMessage } from "./StreamingMessage";
import { AgentChatMessage } from "@/types/agent-chat";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import type { UIMessage } from "@convex-dev/agent/react";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

interface Thread {
  threadId: string;
  title: string;
  preview: string;
  createdAt: number;
  status: string;
  messageCount: number;
  documentId?: string;
  documentType?: string;
}

interface RefactoredAgentChatSidebarProps {
  selectedAgentId?: string;
  isMinimized?: boolean;
  className?: string;
  fillParent?: boolean; // If true, fills parent container instead of fixed w-96
  title?: string; // Custom title (default: "Business Assistant")
  subtitle?: string; // Custom subtitle (default: "Your business support specialist")
  filterThreadsWithDocuments?: boolean; // If true, only show threads with associated documents
  forceShowHistory?: boolean; // If true, force show history view (overrides local state)
}

function RefactoredChatSidebarContent({
  selectedAgentId,
  isMinimized,
  className,
  title = "Business Assistant",
  subtitle = "Your business support specialist",
  filterThreadsWithDocuments = false,
  forceShowHistory,
}: RefactoredAgentChatSidebarProps) {
  // ===== ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (Rules of Hooks) =====

  // Navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Authentication
  const { user, isLoaded } = useUser();

  // Sidebar controls and persistent thread state
  const {
    toggleSidebar,
    hideSidebar,
    threadId,
    setThreadId,
    clearThread,
    resolutionMode,
    exitResolutionMode,
  } = useAgentSidebar();

  // Canvas controls
  const { htmlContent, closeCanvas, setActionHandler, updateCanvasContent } = useAgentCanvas();

  // Organization sync context
  const { localOrganization, isLoading: isOrgSyncLoading } = useOrganizationSync();
  const orgId =
    localOrganization && !localOrganization.isPersonalWorkspace ? localOrganization._id : undefined;
  const personalWorkspaceId = localOrganization?.isPersonalWorkspace
    ? localOrganization._id
    : undefined;

  // Local state management (non-persistent)
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null);
  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [stoppedMessageKeys, setStoppedMessageKeys] = useState<Set<string>>(new Set());
  const [useCanvasMode, setUseCanvasMode] = useState(false);
  const [frozenMessageText, setFrozenMessageText] = useState<Record<string, string>>({});

  const subscription = useQuery(
    api.subscriptions.getUserSubscription,
    user ? {} : "skip"
  );
  const isFreePlan =
    subscription !== undefined &&
    (!subscription || !subscription.amount || subscription.amount === 0 || subscription.status !== "active");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Fetch usage data - now context aware
  const usage = useQuery(
    api.usage.getUsage,
    isOrgSyncLoading
      ? "skip"
      : {
          orgId: orgId as any,
          personalWorkspaceId: personalWorkspaceId as any,
        }
  );

  // Find the next proposal version based on existing ones
  const allProposalsForCanvas = useQuery(
    api.bidProposals.getAllByBidId,
    resolutionMode.active && resolutionMode.protocolId === "generate-proposal" && resolutionMode.contextData?.bidId 
      ? { bidId: resolutionMode.contextData.bidId as any } 
      : "skip"
  );
  const nextProposalVersion = allProposalsForCanvas 
    ? allProposalsForCanvas.reduce((max, p) => Math.max(max, p.version || 1), 0) + 1 
    : 1;

  // Debug: Log showHistory state changes
  useEffect(() => {
    console.log("📜 showHistory state changed:", showHistory);
  }, [showHistory]);

  // Sync showHistory with forceShowHistory prop when provided
  useEffect(() => {
    if (forceShowHistory !== undefined) {
      console.log("📜 Forcing showHistory to:", forceShowHistory);
      setShowHistory(forceShowHistory);
    }
  }, [forceShowHistory]);

  // Toast notifications
  const { toast } = useToast();

  // Determine if this is the Document Writer sidebar (for purple theme)
  const isDocumentWriter = title === "Document Writer" || location.pathname === "/document-writer";

  // Refs for cleanup and auto-expanding textarea
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Create thread mutation
  const createThread = useMutation(api.sidebarChatStreaming.createSidebarThread);

  // Alert mutations
  const updateAlertStatus = useMutation(api.automationHub.updateAlertStatus);

  // Proposal mutations
  const saveCanvasProposal = useMutation(api.proposals.saveCanvasProposal);
  const sendProposalEmail = useAction(api.proposals.sendProposalEmail);

  // Validate thread before using it
  const threadValidation = useQuery(
    api.sidebarChatStreaming.validateThread,
    threadId ? { threadId } : "skip"
  );

  // Get circuit breaker status for the current thread
  const circuitBreakerStatus = useQuery(
    api.circuitBreaker.getCircuitBreakerStatus,
    threadId ? { threadId } : "skip"
  );

  // Resume thread mutation
  const resumeThread = useMutation(api.circuitBreaker.resumeThread);

  const [isResuming, setIsResuming] = useState(false);

  const handleResume = useCallback(async () => {
    if (!threadId) return;
    setIsResuming(true);
    try {
      await resumeThread({ threadId });
      toast({
        title: "Safeguard Reset",
        description: "The conversation has been resumed safely.",
      });
    } catch (err) {
      console.error("Failed to resume thread:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resume conversation. Please try again.",
      });
    } finally {
      setIsResuming(false);
    }
  }, [threadId, resumeThread, toast]);

  const isCircuitBreakerTripped = circuitBreakerStatus?.status === "circuit_breaker_tripped";

  // ✅ SOLUTION 3 ENHANCED: Use useQuery to get full response including streams
  // This allows us to access streaming chunks that usePaginatedQuery was discarding
  const messagesResponse = useQuery(
    api.sidebarChatStreaming.listThreadMessages,
    threadId
      ? {
          threadId,
          paginationOpts: { cursor: null, numItems: 50 },
          streamArgs: { kind: "list" as const }, // Enable streaming via streamArgs
        }
      : "skip"
  );

  // Extract UIMessages and streams from the response
  const { uiMessages, activeStreamIds } = React.useMemo(() => {
    if (!messagesResponse) return { uiMessages: undefined, activeStreamIds: new Set<string>() };

    const messages = messagesResponse.page as UIMessage[];
    const streams = messagesResponse.streams;

    // Create a set of message keys that have active streams
    const streamingMessageKeys = new Set<string>();

    // If there are active streams, track which messages are currently streaming
    if (streams && streams.kind === "list" && streams.messages && streams.messages.length > 0) {
      for (const stream of streams.messages) {
        if (stream.streamId && stream.status === "streaming") {
          // Find the most recent assistant message (last one in the array)
          // This is the message that's currently being streamed
          const assistantMessages = messages.filter((msg) => msg.role === "assistant");
          const streamingMessage = assistantMessages[assistantMessages.length - 1];

          if (streamingMessage) {
            streamingMessageKeys.add(streamingMessage.key);
          }
        }
      }
    }

    return { uiMessages: messages, activeStreamIds: streamingMessageKeys };
  }, [messagesResponse]);

  // Derived state to handle local stopping/freezing of messages
  const displayUiMessages = React.useMemo(() => {
    if (!uiMessages) return undefined;

    // Identify all messages that should be considered "stopped"
    const locallyStoppedKeys = new Set(stoppedMessageKeys);

    // If a user message was stopped, any subsequent assistant message should also be stopped
    for (let i = 1; i < uiMessages.length; i++) {
      const prevMsg = uiMessages[i - 1];
      const m = uiMessages[i];
      if (
        m.role === "assistant" &&
        prevMsg.role === "user" &&
        locallyStoppedKeys.has(prevMsg.key)
      ) {
        locallyStoppedKeys.add(m.key);
      }
    }

    return uiMessages
      .filter((m) => {
        // Hide System Notifications completely
        if (m.role === "user" && m.text?.startsWith("[System Notification]")) {
          return false;
        }

        // Hide assistant messages that were stopped before they started (no frozen text)
        if (
          m.role === "assistant" &&
          locallyStoppedKeys.has(m.key) &&
          frozenMessageText[m.key] === undefined
        ) {
          return false;
        }
        return true;
      })
      .map((m) => {
        let displayContent = m.text || "";

        // Hide large system prompts that were triggered automatically via UI actions
        if (m.role === "user") {
          if (displayContent.includes("We need to generate a digital, interactive proposal for")) {
            const match = displayContent.match(/proposal for (.*?)\./);
            const clientName = match ? match[1] : "the client";
            displayContent = `Generate a digital proposal for ${clientName}`;
          } else if (displayContent.startsWith("[Action Triggered via UI]")) {
            const actionMatch = displayContent.match(/Action: \*\*(.*?)\*\*/);
            const actionName = actionMatch ? actionMatch[1].replace(/_/g, " ") : "UI Action";
            displayContent = `Executed Action: ${actionName}`;
          } else if (displayContent.startsWith("[Resolution Action]")) {
            const firstLine = displayContent.split('\n')[0].replace("[Resolution Action]", "").trim();
            displayContent = `Auto-Action: ${firstLine}`;
          }
        }

        const baseMessage = { ...m, text: displayContent };

        if (locallyStoppedKeys.has(m.key)) {
          return {
            ...baseMessage,
            status: "failed" as const,
            error: "Stopped by user",
            text:
              frozenMessageText[m.key] !== undefined ? frozenMessageText[m.key] : baseMessage.text,
          };
        }
        return baseMessage;
      });
  }, [uiMessages, stoppedMessageKeys, frozenMessageText]);

  // Send message mutation
  const sendMessage = useMutation(api.sidebarChatStreaming.initiateAsyncStreaming);

  // Delete thread mutation
  const deleteThreadMutation = useMutation(api.sidebarChatStreaming.deleteThread);

  // Query for thread history - use filtered query if filterThreadsWithDocuments is true
  const threadsQuery = useQuery(
    filterThreadsWithDocuments
      ? api.sidebarChatStreaming.listUserThreadsWithDocuments
      : api.sidebarChatStreaming.listUserThreads,
    showHistory ? { paginationOpts: { cursor: null, numItems: 50 } } : "skip"
  );

  // ✅ SIMPLIFIED: Convert UIMessages to AgentChatMessage format
  // The useUIMessages hook handles all streaming logic automatically
  // FILTER: Exclude long assistant messages (document content) from sidebar display
  const messages: AgentChatMessage[] = React.useMemo(() => {
    if (!uiMessages || uiMessages.length === 0) {
      return [];
    }

    return uiMessages
      .filter((msg: UIMessage) => {
        // Hide System Notifications completely
        if (msg.role === "user" && msg.text?.startsWith("[System Notification]")) {
          return false;
        }

        // Always show user messages
        if (msg.role === "user") return true;

        // For assistant messages from the Business Assistant, show ALL messages
        // regardless of length (detailed responses are expected and desired)
        if (msg.role === "assistant") {
          // Business Assistant messages should always be shown
          // (This sidebar is specifically for the Business Assistant)
          return true;
        }

        return true; // Show all other messages
      })
      .map((msg: UIMessage): AgentChatMessage => {
        let displayContent = msg.text || "";

        // Hide large system prompts that were triggered automatically via UI actions
        if (msg.role === "user") {
          if (displayContent.includes("We need to generate a digital, interactive proposal for")) {
            const match = displayContent.match(/proposal for (.*?)\./);
            const clientName = match ? match[1] : "the client";
            displayContent = `Generate a digital proposal for ${clientName}`;
          } else if (displayContent.startsWith("[Action Triggered via UI]")) {
            const actionMatch = displayContent.match(/Action: \*\*(.*?)\*\*/);
            const actionName = actionMatch ? actionMatch[1].replace(/_/g, " ") : "UI Action";
            displayContent = `Executed Action: ${actionName}`;
          } else if (displayContent.startsWith("[System Notification]")) {
            displayContent = "System Notification Sent";
          } else if (displayContent.startsWith("[Resolution Action]")) {
            const match = displayContent.match(/\[Resolution Action\] (.*?)\n/);
            displayContent = match ? `Started: ${match[1]}` : "Started Resolving Alert";
          } else if (displayContent.startsWith("System Alert ID:")) {
            displayContent = "Started Resolving Alert";
          }
        }

        return {
          id: msg.key || msg.id || "",
          role: msg.role as "user" | "assistant" | "system",
          content: displayContent,
          timestamp: msg._creationTime || Date.now(),
          metadata: {
            threadId: threadId || undefined,
            agentName: msg.agentName || undefined,
            status: msg.status,
          },
        };
      });
  }, [uiMessages, threadId]);

  // Validate and clear invalid threads
  useEffect(() => {
    if (threadId && threadValidation && !threadValidation.valid) {
      clearThread();
    }
  }, [threadId, threadValidation, clearThread]);

  // Don't auto-create threads on mount
  // Threads will be created when the user sends their first message
  // This prevents creating empty threads when deleting the active thread

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change, but ONLY if already near the bottom
  const scrollToBottom = useCallback((force = false) => {
    if (force === true) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" }); // use auto for smoother streaming
      }
    } else {
      // Fallback if ref isn't attached yet
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Clear isWaitingForResponse when the assistant has finished responding
  useEffect(() => {
    if (displayUiMessages && displayUiMessages.length > 0) {
      const lastMessage = displayUiMessages[displayUiMessages.length - 1];
      if (
        lastMessage.role === "assistant" &&
        (lastMessage.status === "success" || lastMessage.status === "failed")
      ) {
        setIsWaitingForResponse(false);
      }
    }
  }, [displayUiMessages]);

  // Auto-expand textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set height to scrollHeight, but cap at max-height (200px = ~8-10 lines)
      const newHeight = Math.min(textarea.scrollHeight, 200);
      textarea.style.height = `${newHeight}px`;
    }
  }, [prompt]);

  const handleNewConversation = useCallback(() => {
    clearThread();
    setPrompt("");
    setError(null);
    setShowHistory(false);
    setIsWaitingForResponse(false);
    closeCanvas();
  }, [clearThread, closeCanvas]);

  // handleSendMessage defined with an optional override prompt and forceNewThread flag
  const handleSendMessage = useCallback(
    async (overridePrompt?: string, forceNewThread: boolean = false) => {
      const finalPrompt = (overridePrompt || prompt).trim();
      if (!finalPrompt || isLoading) return;

      const purchasedCredits = (usage?.purchasedCredits ?? (subscription as any)?.purchasedCredits ?? 0);
      const hasPurchasedCredits = purchasedCredits > 0;
      const monthlyRemaining = usage ? Math.max(0, usage.creditLimit - usage.creditsUsed) : 0;
      const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);

      // Only block if user has no top-up credits AND no remaining monthly plan credits
      if (!hasPurchasedCredits && (!hasActivePaidPlan || monthlyRemaining <= 0)) {
        setShowUpgradeModal(true);
        return;
      }

      let messageContent = finalPrompt;
      if (useCanvasMode) {
        messageContent = messageContent + "\n\n(Please output this in the interactive canvas)";
        setUseCanvasMode(false);
      }
      
      const activeResolutionMode = resolutionModeRef.current;

      if (!overridePrompt) setPrompt("");
      setIsLoading(true);
      setIsWaitingForResponse(true);
      setError(null);

      try {
        // If no thread exists (or forceNewThread is true), create one first
        let activeThreadId = forceNewThread ? null : threadId;
        if (!activeThreadId) {
          const result = await createThread();
          activeThreadId = result.threadId;
          setThreadId(activeThreadId);

          // Link thread to alert if in resolution mode
          if (activeResolutionMode.active && activeResolutionMode.contextData?.alertId) {
            try {
              await updateAlertStatus({
                alertId: activeResolutionMode.contextData.alertId as any,
                status: "resolving", // Transition to Resolution Stage
                threadId: activeThreadId as any,
              });
            } catch (err) {
              console.error("Failed to link thread to alert:", err);
            }
          }
        }

        // Send message to AI Assistant agent
        console.log("[AI Assistant] Sending message to sidebar chat agent");
        await sendMessage({
          threadId: activeThreadId,
          prompt: messageContent,
          orgId: orgId as any,
          personalWorkspaceId: personalWorkspaceId as any,
        });

        // Add a small delay to make the loading state more visible for debugging
        await new Promise((resolve) => setTimeout(resolve, 500));

        // The messages will be updated automatically via the subscription
      } catch (error: any) {
        console.error("❌ Failed to send message:", error);

        // Check if this is a usage limit exceeded error
        const errorMessage = error?.data?.message || error?.message || "";
        const errorCode = error?.data?.code || "";

        if (
          errorCode === "USAGE_LIMIT_EXCEEDED" ||
          errorMessage.includes("credit limit") ||
          errorMessage.includes("exceeded")
        ) {
          setShowUpgradeModal(true);
        } else {
          setError("Failed to send message. Please try again.");
        }
        setPrompt(messageContent); // Restore the message
        setIsWaitingForResponse(false);
      } finally {
        setIsLoading(false);
      }
    },
    [
      prompt,
      isLoading,
      threadId,
      sendMessage,
      createThread,
      setThreadId,
      usage,
      orgId,
      personalWorkspaceId,
      updateAlertStatus,
      isFreePlan,
    ]
  );

  // Use refs to keep the latest values without triggering useEffect rebuilds
  const htmlContentRef = useRef(htmlContent);
  useEffect(() => {
    htmlContentRef.current = htmlContent;
  }, [htmlContent]);

  const resolutionModeRef = useRef(resolutionMode);
  useEffect(() => {
    resolutionModeRef.current = resolutionMode;
  }, [resolutionMode]);

  // Setup Canvas Action Handler
  useEffect(() => {
    // We must wrap the function in another function because setActionHandler is a React state setter
    setActionHandler(() => async (action: string, payload: any) => {
      if (!action || action === "null" || action === "undefined") {
        console.warn("⚠️ Blocked empty/null action from canvas:", action);
        return;
      }
      console.log("🎯 Received Action from Canvas:", action, payload);

      // Native Frontend Interception for Proposals
      if (action === "save_proposal" || action === "approve_proposal") {
        let payloadBidId = (payload as any)?.bidId;
        
        // Fallback to resolution mode context if AI hallucinated payload
        if (!payloadBidId && resolutionModeRef.current.active && resolutionModeRef.current.protocolId === "generate-proposal") {
          payloadBidId = resolutionModeRef.current.contextData?.bidId;
        }

        if (!payloadBidId) {
          toast({
            title: "Error",
            description: "Missing bid ID in payload and context.",
            variant: "destructive",
          });
          return;
        }

        const currentHtmlContent = htmlContentRef.current;
        if (!currentHtmlContent) {
          toast({
            title: "Error",
            description: "No HTML content found to save.",
            variant: "destructive",
          });
          return;
        }

        try {
          setIsLoading(true);
          const proposalId = await saveCanvasProposal({
            bidId: payloadBidId,
            htmlContent: currentHtmlContent,
          });

          if (action === "save_proposal") {
            toast({ title: "Success", description: "Proposal saved to drafts!" });
            
            // Replace the button with a success message in the canvas HTML
            const updatedHtml = currentHtmlContent.replace(
              /<button data-action="save_proposal"[^>]*>[\s\S]*?<\/button>/g,
              '<span style="color: #16a34a; margin-right: 12px; font-weight: 500;">✓ Draft Saved</span>'
            );
            updateCanvasContent(updatedHtml, false);
            
          } else if (action === "approve_proposal") {
            toast({ title: "Sending...", description: "Sending proposal to client..." });
            await sendProposalEmail({ proposalId });
            toast({ title: "Success", description: "Proposal sent to client successfully!" });

            // Replace the button with a success message in the canvas HTML
            const updatedHtml = currentHtmlContent.replace(
              /<button data-action="approve_proposal"[^>]*>[\s\S]*?<\/button>/g,
              '<span style="color: #16a34a; font-weight: 500;">✓ Approved & Sent to Client</span>'
            );
            updateCanvasContent(updatedHtml, false);

            // Optionally tell the agent what happened
            handleSendMessage(
              `[System Notification] I have successfully approved the proposal and sent it to the client via email (via Resend) for Bid ID: ${payloadBidId}.`
            );
          }
        } catch (err: any) {
          console.error("Failed to process proposal action:", err);
          toast({
            title: "Error",
            description: err.message || "Failed to process proposal.",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      const payloadString = payload ? JSON.stringify(payload) : "no payload";

      // Send a visible request message to the chat
      // We explicitly instruct the agent to execute the user's intended action
      const prompt = `[Action Triggered via UI] The user clicked a button in the interactive canvas to trigger the following action:\n\nAction: **${action}**\nPayload: \`${payloadString}\`\n\nPlease execute this action immediately using your tools.`;

      handleSendMessage(prompt);
    });

    return () => {
      setActionHandler(() => null);
    };
  }, [handleSendMessage, setActionHandler, saveCanvasProposal, sendProposalEmail, toast]);

  // EFFECT: Handle Resolution Mode Trigger
  useEffect(() => {
    if (resolutionMode.active && resolutionMode.protocolId === "satisfaction-guard" && !isLoading) {
      const { rating, comment, locationId } = resolutionMode.contextData;

      const resolutionPrompt = `[Resolution Action] Resolving Satisfaction Guard Alert for Location ${locationId || "Unknown"}
---
System Alert ID: ${resolutionMode.contextData.alertId}
I need to draft a professional Service Recovery Plan for a client who just gave us a ${rating}-star review. 
      
Review Feedback: "${comment}"

Please perform the following two actions in order WITHOUT stopping or asking for permission between steps:
1. Draft a comprehensive recovery plan using your interactive canvas (do not generate a PDF).
2. Use your connected email tools (e.g., Gmail) to draft a professional apology and check-in email to the client, and save it as a draft (or send it if instructed). If you cannot connect to email, draft it in the chat.`;

      // Clear current thread and start fresh for resolution
      handleNewConversation();

      // Small timeout to ensure state settles before sending
      const timer = setTimeout(() => {
        handleSendMessage(resolutionPrompt, true);
        exitResolutionMode();
      }, 500);

      return () => clearTimeout(timer);
    }

    if (resolutionMode.active && resolutionMode.protocolId === "zero-miss-shift" && !isLoading) {
      const { expectedStart, employeeId, locationId } = resolutionMode.contextData;

      const resolutionPrompt = `[Resolution Action] Resolving Missed Shift Alert for Employee ${employeeId}
---
System Alert ID: ${resolutionMode.contextData.alertId}
Location ID: ${locationId}
Scheduled Start: ${expectedStart}

Please perform the following two actions in order WITHOUT stopping or asking for permission between steps:
1. Use your connected tools to send a polite but firm text message / notification to the employee regarding the missed clock-in.
2. Use your connected email tools (e.g., Gmail) to draft a professional "Service Delay" notice for the client at this location, explaining that we are working on a replacement. Save it as a draft for my review.

Check our standard operating procedures in the knowledge base for 'No-Show' protocols before drafting.`;

      handleNewConversation();
      const timer = setTimeout(() => {
        handleSendMessage(resolutionPrompt, true);
        exitResolutionMode();
      }, 500);

      return () => clearTimeout(timer);
    }

    if (resolutionMode.active && resolutionMode.protocolId === "lead-reviver" && !isLoading) {
      const { leadName, stagnationDays, industry, opportunityDescription } =
        resolutionMode.contextData;

      const resolutionPrompt = `[Resolution Action] Resolving Lead Stagnation Alert for "${leadName}"
---
System Alert ID: ${resolutionMode.contextData.alertId}
This is a ${industry} lead that has been stagnant for over ${stagnationDays} days.
Opportunity Details: ${opportunityDescription || "N/A"}

Please perform the following THREE actions in order WITHOUT stopping or asking for permission between steps:
1. Draft a "Value Drop" capability statement using your interactive canvas that highlights our experience in the ${industry} sector.
2. Use your connected email tools (e.g., Gmail) to draft a professional, personalized check-in email to the lead and save it as a draft in my email account. The email should focus on solving their specific needs while acknowledging the time that has passed, and must include a placeholder for a link to the drafted Value Drop capability statement.
3. Use the 'addBusinessNote' tool to add a note to the lead's detail page indicating that a re-engagement email was drafted and a capability statement was generated.

Ensure the tone is professional but persistent, focused on re-opening the conversation. Do NOT stop after gathering information; you must complete all three steps immediately.`;

      handleNewConversation();
      const timer = setTimeout(() => {
        handleSendMessage(resolutionPrompt, true);
        exitResolutionMode();
      }, 500);

      return () => clearTimeout(timer);
    }

    if (resolutionMode.active && resolutionMode.protocolId === "supply-threshold" && !isLoading) {
      const { lowItems, alertId } = resolutionMode.contextData;

      const itemsList = (lowItems || []).map((item: any) => 
        `- ${item.itemName} at ${item.locationName} (Current: ${item.currentQuantity} ${item.unit}, Min: ${item.minimumRequired} ${item.unit}, Vendor: ${item.vendor})`
      ).join('\n');

      const resolutionPrompt = `[Resolution Action] Resolving Supply Replenishment Alert
---
System Alert ID: ${alertId || "Unknown"}

Low Supplies Identified:
${itemsList}

Please perform the following two actions in order WITHOUT stopping or asking for permission between steps:
1. Draft a comprehensive "Supply Replenishment Report" using your interactive canvas grouped by Location and Vendor that includes:
   - A list of each location and what items they need from each vendor.
   - A list of all items and quantities to be ordered from each vendor (calculate a reasonable reorder quantity e.g. 2x minimum).
   - A cost breakdown for each list and location.
2. Use your connected email tools (e.g., Gmail) to draft an email to the vendor(s) to request these supplies and save it as a draft. After the email is drafted, ask me if I would like you to generate a formal purchase order.

Reference our standard procurement policies in the knowledge base if available.`;

      handleNewConversation();
      const timer = setTimeout(() => {
        handleSendMessage(resolutionPrompt, true);
        exitResolutionMode();
      }, 500);

      return () => clearTimeout(timer);
    }

    if (resolutionMode.active && resolutionMode.protocolId === "generate-proposal" && !isLoading) {
      const {
        bidId,
        businessId,
        clientName,
        templateStyle,
        tone,
        pricingInstruction,
        focusAreasText,
        customInstructions,
        sectionsList,
        bidDataContext,
      } = resolutionMode.contextData;

      const clientInfo = bidDataContext?.client || {};
      const businessInfo = bidDataContext?.business || {}; // The client's business info
      const organizationInfo = bidDataContext?.organization || {}; // The user's organization info

      const businessName = organizationInfo.name || organizationInfo.companyName || "Our Company";
      const primaryColor = organizationInfo.primaryColor || "#6366f1";
      const secondaryColor = organizationInfo.secondaryColor || "#1e293b";
      const logoUrl = organizationInfo.logo || "";
      const logoDarkUrl = organizationInfo.logoDark || "";

      const bidContextString = bidDataContext
        ? `\n\n--- ACTUAL BID DETAILS ---\nPlease use the following EXACT details for services, pricing, facility size, client, business info, and location when generating the proposal:\n${JSON.stringify(bidDataContext, null, 2)}\n----------------------\n`
        : "";

      const sectionsArray = sectionsList ? sectionsList.split(", ") : ["Scope of Work", "Pricing"];

      let dynamicHtmlSections = "";
      sectionsArray.forEach((section) => {
        if (section.toLowerCase().includes("scope of work")) {
          dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Scope of Work</h2>
    <div style="background: #f8fafc; padding: 20px; border-radius: 8px;">
      [Insert beautifully styled HTML lists or tables for the services based on the context data]
    </div>
  </div>`;
        } else if (section.toLowerCase().includes("pricing")) {
          dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Pricing (${pricingInstruction})</h2>
    <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; border-left: 4px solid ${primaryColor};">
      [Insert detailed pricing table or summary here based on the context data]
    </div>
  </div>`;
        } else if (section.toLowerCase().includes("cover letter")) {
          dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Cover Letter</h2>
    <div style="line-height: 1.6; color: #475569;">
      [Format this exactly like a formal business letter. Include today's date, the client company (${clientName || "The Client"}), the client's address from context, a formal salutation to the contact person, professional body paragraphs, and a formal sign-off explicitly signed by "${organizationInfo.contactName || "The Team"}" at "${businessName}". Ensure proper paragraph spacing.]
    </div>
  </div>`;
        } else if (
          section.toLowerCase().includes("authorization") ||
          section.toLowerCase().includes("authorized")
        ) {
          dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Authorization</h2>
    <div style="line-height: 1.6; color: #475569;">
      <p style="margin-bottom: 16px;">This proposal is valid for 30 days from the date of submission. Acceptance of this proposal constitutes agreement to the terms and conditions outlined herein. Upon approval, a formal service agreement will be executed by both parties.</p>
      <div style="border-top: 1px solid #e2e8f0; margin-top: 24px; padding-top: 16px;">
        <strong>Authorized Representative:</strong><br>
        ${clientName || "The Client"}<br>
        [Insert Client Phone/Email/Address from the context data here]<br>
        Date: [Insert Current Date]
      </div>
    </div>
  </div>`;
        } else if (section.toLowerCase().includes("why choose us")) {
          if (focusAreasText) {
            dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Why Choose Us</h2>
    <div style="line-height: 1.6; color: #475569;">
      [Based on the instruction: "${focusAreasText}", treat each focus area as a distinct subsection. For EACH focus area, provide a creative, engaging subtitle (formatted as a bold sub-header like <h3 style="font-size: 18px; color: ${secondaryColor}; margin-top: 16px; margin-bottom: 8px;">Subtitle</h3>) followed by one to two paragraphs elaborating on how that specific selling point relates to the client's needs.]
    </div>
  </div>`;
          } else {
            dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">Why Choose Us</h2>
    <div style="line-height: 1.6; color: #475569;">
      [Write compelling, professional content for the Why Choose Us section here. Separate paragraphs clearly.]
    </div>
  </div>`;
          }
        } else {
          dynamicHtmlSections += `
  <div style="margin-bottom: 40px; padding: 30px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.02);">
    <h2 style="font-size: 24px; color: ${primaryColor}; margin-top: 0; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">${section}</h2>
    <div style="line-height: 1.6; color: #475569;">
      [Write compelling, professional content for the ${section} section here. Separate paragraphs clearly.]
    </div>
  </div>`;
        }
      });

      const resolutionPrompt = `System Alert ID: ${resolutionMode.contextData.alertId}
We need to generate a digital, interactive proposal for ${clientName || "the client"}.
You MUST return the proposal as a single markdown HTML code block. DO NOT use plain text markdown.

${bidContextString}
${tone ? `Tone Instruction: Ensure the entire proposal is written in a ${tone} tone.` : ""}
${customInstructions ? `Additional Instructions: ${customInstructions}` : ""}

CRITICAL DESIGN INSTRUCTION:
- You are generating a beautiful, premium "presentation-style" HTML document.
- Here are the business's available logos: 
  - Standard Logo URL: ${logoUrl ? logoUrl : "None"}
  - Dark-Background Logo URL: ${logoDarkUrl ? logoDarkUrl : "None"}
- You must carefully design the "Hero Section" header in the HTML below. Decide what background color to use. If you use a dark background color, you MUST use the Dark-Background Logo (if available) and white text. If you use a light background color, use the Standard Logo and dark text.
- If neither logo is available, just use the business name "${businessName}" as a text header.
- Ensure proper paragraph spacing throughout the document by wrapping text in <p style="margin-bottom: 16px;">.

CRITICAL INSTRUCTION: You must strictly use the following HTML template structure for your output. Fill in the data, services, and pricing based on the context above.

\`\`\`html
<div style="font-family: 'Inter', sans-serif; background: #f8fafc; color: #1e293b; max-width: 850px; margin: 0 auto; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.08);">
  
  <div class="admin-only-actions" style="padding: 16px 40px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: flex-end; gap: 12px; background: #ffffff;">
    <div style="flex-grow: 1; font-weight: 600; color: #64748b; font-size: 14px;">Proposal v${nextProposalVersion}</div>
    <button data-action="save_proposal" data-payload='{"bidId": "${bidId}"}' class="px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors">Save as Draft</button>
    <button data-action="approve_proposal" data-payload='{"bidId": "${bidId}"}' class="px-4 py-2 bg-[#6366f1] rounded text-white font-medium shadow cursor-pointer hover:bg-indigo-600 transition-colors">Approve & Send</button>
  </div>

  <!-- HERO SECTION (DESIGN THIS: Set background-color, pick the right logo, pick the right text color) -->
  <div style="background-color: ${primaryColor}; padding: 60px 40px; text-align: center; position: relative;">
    [DESIGNER: Insert the chosen logo image here with max-height 80px, OR the text "${businessName}" if no logo]
    <h2 style="color: #ffffff; margin: 24px 0 0 0; font-size: 36px; font-weight: 800; letter-spacing: -0.02em;">Service Proposal</h2>
    <p style="color: rgba(255,255,255,0.9); font-size: 18px; margin-top: 16px;">Prepared for: ${clientName || "The Client"}</p>
    <div style="width: 60px; height: 4px; background: #ffffff; margin: 30px auto 0; border-radius: 2px; opacity: 0.8;"></div>
  </div>

  <!-- CONTENT -->
  <div style="padding: 50px 40px; background: #f8fafc;">
    ${dynamicHtmlSections}
  </div>

  <!-- FOOTER -->
  <div style="background: #1e293b; padding: 30px 40px; color: #94a3b8; font-size: 14px; text-align: center; border-top: 1px solid #334155;">
    <p style="margin: 0;">&copy; [Insert Current Year] ${businessName}. All rights reserved.</p>
    <p style="margin: 8px 0 0 0;">[Insert Business Email] | [Insert Business Phone] | [Insert Business Website]</p>
  </div>

  <div class="admin-only-actions" style="margin-top: 40px; padding: 20px; border-top: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: flex-end; gap: 12px; background: #ffffff;">
    <div style="flex-grow: 1; font-weight: 600; color: #64748b; font-size: 14px;">Proposal v${nextProposalVersion}</div>
    <button data-action="save_proposal" data-payload='{"bidId": "${bidId}"}' class="px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors">Save as Draft</button>
    <button data-action="approve_proposal" data-payload='{"bidId": "${bidId}"}' class="px-4 py-2 bg-[#6366f1] rounded text-white font-medium shadow cursor-pointer hover:bg-indigo-600 transition-colors">Approve & Send</button>
  </div>
</div>
\`\`\`

If you understand, output ONLY the completed HTML block wrapped in \`\`\`html fences. Do NOT add any extra markdown text outside the code block.`;

      handleNewConversation();
      const timer = setTimeout(() => {
        handleSendMessage(resolutionPrompt, true);
        exitResolutionMode();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    resolutionMode.active,
    resolutionMode.protocolId,
    isLoading,
    handleSendMessage,
    handleNewConversation,
    exitResolutionMode,
  ]);

  // Handle showing history view
  const handleShowHistory = useCallback(() => {
    console.log("📜 Chat History button clicked");
    setShowHistory(true);
    setError(null);
    closeCanvas();
  }, [closeCanvas]);

  // Handle returning to chat view
  const handleBackToChat = useCallback(() => {
    setShowHistory(false);
  }, []);

  // Handle switching to a different thread
  const handleSwitchThread = useCallback(
    (newThreadId: string, hasDocument?: boolean) => {
      // Check if this thread has an associated document
      // Use the passed parameter (from thread list which includes hasDocument flag)
      const threadHasDoc = hasDocument || false;
      const isDocumentWriterPage = location.pathname === "/document-writer";

      console.log(
        "🔄 Switching thread:",
        newThreadId,
        "hasDocument:",
        threadHasDoc,
        "isDocumentWriterPage:",
        isDocumentWriterPage
      );

      // Close the canvas when switching threads to prevent overlap
      closeCanvas();

      if (threadHasDoc && !isDocumentWriterPage) {
        // Navigate to Document Writer page with the thread ID and document flag in state
        console.log("📄 Navigating to Document Writer with thread:", newThreadId);

        // FIX: Clear Business Assistant's thread when switching to Document Writer
        // The Document Writer page will handle setting its own context's threadId
        clearThread();
        setShowHistory(false);
        setError(null);

        // Navigate with state to indicate this is loading an existing conversation with a document
        navigate("/document-writer", {
          state: {
            loadThreadId: newThreadId,
            hasDocument: true,
            fromChatHistory: true,
          },
        });
      } else {
        // Normal thread switching (no document or already on Document Writer page)
        setThreadId(newThreadId);
        setShowHistory(false);
        setError(null);
      }
    },
    [setThreadId, clearThread, location.pathname, navigate, closeCanvas]
  );

  // Handle deleting a thread
  const confirmDeleteThread = useCallback(
    (threadIdToDelete: string, e: React.MouseEvent) => {
      // Stop event propagation to prevent triggering thread selection
      e.stopPropagation();
      setThreadToDelete(threadIdToDelete);
    },
    []
  );

  const executeDeleteThread = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
      }
      if (!threadToDelete) return;
      
      setDeletingThreadId(threadToDelete);
      console.log("Starting deletion of thread:", threadToDelete);

      try {
        await deleteThreadMutation({ threadId: threadToDelete });

        // If we deleted the currently active thread, clear it
        if (threadToDelete === threadId) {
          clearThread();

          // If we're on the Document Writer page, navigate back to dashboard
          // This prevents the infinite spinner issue when the document/thread is deleted
          if (location.pathname === "/document-writer") {
            console.log(
              "📍 Deleted active thread on Document Writer page, navigating to dashboard"
            );
            navigate("/dashboard");
          }

          closeCanvas();
        }

        console.log("Successfully deleted thread:", threadToDelete);
        toast({
          title: "Conversation deleted",
          description: "The conversation history was permanently deleted.",
          duration: 3000,
        });
        
        // Match DocumentWriterSidebar behavior: hide history on delete
        setShowHistory(false);
      } catch (error) {
        console.error("❌ Failed to delete thread:", error);
        toast({
          title: "Failed to delete conversation",
          description: error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setDeletingThreadId(null);
        setThreadToDelete(null);
      }
    },
    [threadToDelete, threadId, deleteThreadMutation, clearThread, toast, location.pathname, navigate, closeCanvas]
  );

  // Handle key press for textarea (not a hook, just a regular function)
  // Shift+Enter for new line, Enter to send
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Shift+Enter allows new line (default textarea behavior)
  };

  const handleStop = useCallback(() => {
    setIsLoading(false);
    setIsWaitingForResponse(false);

    if (uiMessages && uiMessages.length > 0) {
      const lastMessage = uiMessages[uiMessages.length - 1];

      // If waiting for assistant, tag the last user message as stopped
      if (lastMessage.role === "user") {
        setStoppedMessageKeys((prev) => new Set(prev).add(lastMessage.key));
      }

      // Freeze any currently generating assistant messages
      const generatingMsgs = uiMessages.filter(
        (m) => m.status === "in_progress" || m.status === "streaming" || activeStreamIds.has(m.key)
      );
      if (generatingMsgs.length > 0) {
        setStoppedMessageKeys((prev) => {
          const next = new Set(prev);
          generatingMsgs.forEach((m) => next.add(m.key));
          return next;
        });
        setFrozenMessageText((prev) => {
          const next = { ...prev };
          generatingMsgs.forEach((m) => {
            next[m.key] = m.text || "";
          });
          return next;
        });
      }
    }
  }, [uiMessages, activeStreamIds]);

  const isGenerating =
    isLoading ||
    isWaitingForResponse ||
    !!displayUiMessages?.some((m) => m.status === "in_progress" || m.status === "streaming");

  // ===== ALL HOOKS CALLED - NOW SAFE TO DO EARLY RETURNS =====

  // Don't render anything until authentication is loaded
  if (!isLoaded) {
    return (
      <div className="flex h-full items-center justify-center">
        <div
          className={cn(
            "h-6 w-6 animate-spin rounded-full border-b-2",
            isDocumentWriter ? "border-purple-600" : "border-blue-600"
          )}
        ></div>
      </div>
    );
  }

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <div
          className={cn(
            "mb-4 flex h-12 w-12 items-center justify-center rounded-lg",
            isDocumentWriter ? "bg-purple-100" : "bg-blue-100"
          )}
        >
          <Bot className={cn("h-6 w-6", isDocumentWriter ? "text-purple-600" : "text-blue-600")} />
        </div>
        <h3 className="mb-2 font-semibold text-gray-900">Sign In Required</h3>
        <p className="mb-4 text-sm text-gray-600">
          Please sign in to start chatting with your business assistant.
        </p>
        <Button onClick={() => (window.location.href = "/sign-in")} size="sm">
          Sign In
        </Button>
      </div>
    );
  }

  // Show empty state when there's no thread (e.g., after deletion)
  // Thread will be created when user sends their first message
  if (!threadId) {
    return (
      <div className="flex h-full flex-col">
        {/* Header - Matches main chat view styling */}
        <div
          className={cn(
            "flex-shrink-0 border-b border-gray-200 bg-gradient-to-r p-4",
            isDocumentWriter ? "from-purple-50 to-violet-50" : "from-blue-50 to-indigo-50"
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg",
                  isDocumentWriter ? "bg-purple-100" : "bg-blue-100"
                )}
              >
                <Bot
                  className={cn("h-4 w-4", isDocumentWriter ? "text-purple-600" : "text-blue-600")}
                />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{title}</h2>
                <p className="text-xs text-gray-600">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={hideSidebar}
                className="h-8 w-8 p-0 hover:bg-white/50"
                title="Close Sidebar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the conversation history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={!!deletingThreadId}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={executeDeleteThread}
                disabled={!!deletingThreadId}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingThreadId ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Show history view or empty state */}
        {showHistory ? (
          /* History View */
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="mb-4 px-1 text-lg font-semibold text-gray-900">Conversation History</h3>
            {/* Only show back button when not forced to show history */}
            {!forceShowHistory && (
              <Button variant="ghost" size="sm" onClick={handleBackToChat} className="mb-4 gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Chat
              </Button>
            )}

            {!threadsQuery ? (
              <div className="flex items-center justify-center py-8">
                <Loader2
                  className={cn(
                    "h-6 w-6 animate-spin",
                    isDocumentWriter ? "text-purple-600" : "text-blue-600"
                  )}
                />
              </div>
            ) : threadsQuery.threads.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <MessageSquare className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <p className="mb-2 text-sm font-medium">No Previous Conversations</p>
                <p className="text-xs">
                  Start a new conversation to begin chatting with your business assistant.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {threadsQuery.threads.map((thread: Thread) => (
                  <div
                    key={thread.threadId}
                    className={cn(
                      "group cursor-pointer rounded-xl border-2 p-4 transition-all",
                      thread.threadId === threadId
                        ? "border-blue-200 bg-blue-50 shadow-sm"
                        : "border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50"
                    )}
                    onClick={() =>
                      handleSwitchThread(thread.threadId, !!(thread as any).documentId)
                    }
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="flex-shrink-0 rounded-lg bg-blue-50 p-2 text-blue-600">
                          <MessageSquare className="h-4 w-4" />
                        </div>
                        <h4 className="truncate font-medium text-gray-900">
                          {thread.title || "Untitled Conversation"}
                        </h4>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteThread(thread.threadId, e);
                        }}
                        disabled={deletingThreadId === thread.threadId}
                        className="h-8 w-8 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        {deletingThreadId === thread.threadId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="pl-11 text-xs text-gray-500">
                      {new Date(thread.createdAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Empty state */
          <>
            <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
              <div
                className={cn(
                  "mb-4 flex h-16 w-16 items-center justify-center rounded-full",
                  isDocumentWriter ? "bg-purple-100" : "bg-blue-100"
                )}
              >
                <MessageSquare
                  className={cn("h-8 w-8", isDocumentWriter ? "text-purple-600" : "text-blue-600")}
                />
              </div>
              <h3 className="mb-2 font-semibold text-gray-900">Start a Conversation</h3>
              <p className="mb-6 max-w-sm text-sm text-gray-600">
                Ask me anything about your business, and I'll help you find the information you
                need.
              </p>
            </div>

            {/* Input area */}
            <div className="border-t border-gray-200 p-4">
              <div className="mb-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNewConversation}
                  className={cn(
                    "flex-1 border-2",
                    isDocumentWriter
                      ? "border-purple-100 text-purple-600 hover:border-purple-200 hover:bg-purple-50"
                      : "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                  )}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Conversation
                </Button>
                {!forceShowHistory && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowHistory}
                    className={cn(
                      "flex-1 border-2",
                      isDocumentWriter
                        ? "border-purple-100 text-purple-600 hover:border-purple-200 hover:bg-purple-50"
                        : "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                    )}
                  >
                    <History className="mr-2 h-4 w-4" />
                    History
                  </Button>
                )}
              </div>
              <div className="flex flex-col w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm focus-within:ring-1 focus-within:ring-slate-300 transition-shadow">
                <Textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask me anything about your business..."
                  className="max-h-[200px] min-h-[44px] w-full resize-none border-0 bg-transparent px-3 py-2 text-sm shadow-none focus-visible:ring-0"
                  rows={1}
                  disabled={isLoading}
                />
                <div className="flex items-center justify-between px-1 pb-1 pt-2">
                  <div className="flex items-center gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800" disabled={isLoading}>
                          <Plus className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56 mb-2" side="top">
                        <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                          <Paperclip className="h-4 w-4" />
                          <span>Upload files</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                          <HardDrive className="h-4 w-4" />
                          <span>Add from Drive</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                          <Book className="h-4 w-4" />
                          <span>Notebooks</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                          <ImageIcon className="h-4 w-4" />
                          <span>Create image</span>
                          <span className="ml-auto text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm font-medium">New</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="gap-2 cursor-pointer text-slate-800 font-medium bg-slate-50"
                          onClick={() => {
                            setUseCanvasMode(true);
                            setTimeout(() => textareaRef.current?.focus(), 0);
                          }}
                        >
                          <PenSquare className="h-4 w-4 text-blue-600" />
                          <span>Canvas</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                          <MoreHorizontal className="h-4 w-4" />
                          <span>More tools</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {useCanvasMode && (
                      <div className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        <PenSquare className="h-3.5 w-3.5 text-slate-600" />
                        <span>Canvas</span>
                        <button 
                          onClick={(e) => { e.preventDefault(); setUseCanvasMode(false); }} 
                          className="ml-0.5 rounded-full hover:bg-slate-200 p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={isGenerating ? handleStop : () => handleSendMessage()}
                    disabled={!prompt.trim() && !isGenerating}
                    size="icon"
                    className={cn(
                      "h-8 w-8 rounded-full text-white transition-all duration-200",
                      isDocumentWriter
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {isGenerating ? (
                      <Square className="h-4 w-4 fill-current" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}



        <UpgradePromptDialog
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title="Upgrade to Starter"
          description={
            isDocumentWriter
              ? "Use the AI Document Writer to automatically generate, format, and review important business documents."
              : "Access your AI Business Assistant to get expert advice, automate tasks, and draft documents instantly."
          }
          feature={isDocumentWriter ? "AI Document Writer" : "AI Business Assistant"}
          planName="Starter"
          planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
        />
      </div>
    );
  }

  // FIX: Restructured to match original AgentChatSidebar pattern
  // Header, messages, and input must all be in the same flex container
  // for proper sticky header/footer behavior
  return (
    <div className="flex h-full flex-1 flex-col">
      {/* Fixed Header - Always Visible (matches original structure) */}
      <div
        className={cn(
          "flex-shrink-0 border-b border-gray-200 bg-gradient-to-r p-4",
          isDocumentWriter ? "from-purple-50 to-violet-50" : "from-blue-50 to-indigo-50"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-lg",
                isDocumentWriter ? "bg-purple-100" : "bg-blue-100"
              )}
            >
              <Bot
                className={cn("h-4 w-4", isDocumentWriter ? "text-purple-600" : "text-blue-600")}
              />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="h-8 w-8 p-0 hover:bg-white/50"
              title="Close Sidebar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>



      <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the conversation history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingThreadId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeDeleteThread}
              disabled={!!deletingThreadId}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingThreadId ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description={
          isDocumentWriter
            ? "Use the AI Document Writer to automatically generate, format, and review important business documents."
            : "Access your AI Business Assistant to get expert advice, automate tasks, and draft documents instantly."
        }
        feature={isDocumentWriter ? "AI Document Writer" : "AI Business Assistant"}
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />

      {/* Scrollable Content Area - matches original pattern */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {showHistory ? (
          /* History View */
          <div className="flex-1 overflow-y-auto p-4">
            {/* Only show back button when not forced to show history */}
            {!forceShowHistory && (
              <div className="mb-4 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToChat}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Chat
                </Button>
              </div>
            )}

            <h3 className="mb-4 text-lg font-semibold text-gray-900">Conversation History</h3>

            {!threadsQuery ? (
              <div className="flex items-center justify-center py-8">
                <Loader2
                  className={cn(
                    "h-6 w-6 animate-spin",
                    isDocumentWriter ? "text-purple-600" : "text-blue-600"
                  )}
                />
              </div>
            ) : threadsQuery.threads.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <p className="mb-2 text-lg font-medium">No Previous Conversations</p>
                  <p className="text-sm">
                    Start a new conversation to begin chatting with your AI assistant.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {threadsQuery.threads.map((thread: Thread) => (
                  <div
                    key={thread.threadId}
                    onClick={() =>
                      handleSwitchThread(thread.threadId, !!(thread as any).documentId)
                    }
                    className={cn(
                      "group w-full cursor-pointer rounded-lg border p-4 text-left transition-all duration-200",
                      isDocumentWriter
                        ? "hover:border-purple-300 hover:bg-purple-50"
                        : "hover:border-blue-300 hover:bg-blue-50",
                      thread.threadId === threadId
                        ? isDocumentWriter
                          ? "border-purple-500 bg-purple-50"
                          : "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white"
                    )}
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <h4 className="line-clamp-1 flex-1 font-medium text-gray-900">
                        {thread.title}
                      </h4>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        {thread.threadId === threadId && (
                          <span
                            className={cn(
                              "rounded px-2 py-0.5 text-xs font-medium text-white",
                              isDocumentWriter ? "bg-purple-600" : "bg-blue-600"
                            )}
                          >
                            Active
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => confirmDeleteThread(thread.threadId, e)}
                          disabled={deletingThreadId === thread.threadId}
                          className={cn(
                            "h-7 w-7 p-0 opacity-0 transition-opacity group-hover:opacity-100",
                            deletingThreadId === thread.threadId && "opacity-100"
                          )}
                          title="Delete conversation"
                        >
                          {deletingThreadId === thread.threadId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>
                        {new Date(thread.createdAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span>{thread.messageCount} messages</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Chat View */
          <>
            {/* Messages area - scrollable */}
            <div className="flex-1 overflow-y-auto p-4" ref={scrollContainerRef}>
              {displayUiMessages && displayUiMessages.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {/* ✅ OFFICIAL PATTERN: Pass UIMessage directly to StreamingMessage */}
                  {/* Business Assistant: Show ALL messages regardless of length */}
                  {/* (Detailed responses are expected and desired for business queries) */}
                  {displayUiMessages.map((msg: UIMessage) => (
                    <StreamingMessage
                      key={msg.key}
                      message={msg}
                      theme={isDocumentWriter ? "purple" : "blue"}
                      threadId={threadId}
                      agentType={isDocumentWriter ? "document-writer" : "business-assistant"}
                      onUpdate={scrollToBottom}
                    />
                  ))}

                  {/* ✅ Removed TypingIndicator - StreamingMessage component handles "Thinking" state */}

                  {/* Show immediate thinking indicator if waiting for assistant to start responding */}
                  {isWaitingForResponse &&
                    (!displayUiMessages ||
                      displayUiMessages.length === 0 ||
                      displayUiMessages[displayUiMessages.length - 1].role === "user") && (
                      <StreamingMessage
                        message={
                          {
                            key: "thinking-placeholder",
                            role: "assistant",
                            text: "",
                            status: "in_progress",
                          } as any
                        }
                        theme={isDocumentWriter ? "purple" : "blue"}
                        threadId={threadId}
                        agentType={isDocumentWriter ? "document-writer" : "business-assistant"}
                        onUpdate={scrollToBottom}
                      />
                    )}

                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Bot className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                    <p className="mb-2 text-lg font-medium">{title}</p>
                    <p className="text-sm">
                      Start a conversation to get help with your business...
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Error display */}
        {error && (
          <div className="flex-shrink-0 border-t border-red-200 bg-red-50 px-4 py-2">
            <div className="flex items-center text-sm text-red-700">
              <AlertCircle className="mr-2 h-4 w-4" />
              {error}
            </div>
          </div>
        )}

        {/* Fixed Input Area at Bottom - only show in chat view */}
        {!showHistory && (
          <div className="flex-shrink-0 border-t bg-white p-4">
            {isCircuitBreakerTripped && (
              <div className="mb-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-amber-900 shadow-sm">
                <div className="flex gap-2.5">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <div className="space-y-1 text-xs">
                    <p className="font-semibold text-amber-900">System Safeguard</p>
                    <p className="leading-relaxed text-amber-800">
                      Dynamic adjustments paused to prevent an infinite loop. Please review your
                      last prompt or click 'Resume' to continue safely.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isResuming}
                  onClick={handleResume}
                  className="shadow-xs flex h-8 w-full items-center justify-center gap-1 rounded-lg border-amber-300 bg-white text-xs font-semibold text-amber-800 transition-colors hover:border-amber-400 hover:bg-amber-100"
                >
                  {isResuming ? (
                    <>
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin text-amber-600" />
                      Resuming...
                    </>
                  ) : (
                    "Resume Conversation"
                  )}
                </Button>
              </div>
            )}

            <div className="mb-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleNewConversation}
                disabled={isCircuitBreakerTripped}
                className={cn(
                  "flex-1 border-2",
                  isDocumentWriter
                    ? "border-purple-100 text-purple-600 hover:border-purple-200 hover:bg-purple-50"
                    : "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                )}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Conversation
              </Button>
              {!forceShowHistory && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowHistory}
                  className={cn(
                    "flex-1 border-2",
                    isDocumentWriter
                      ? "border-purple-100 text-purple-600 hover:border-purple-200 hover:bg-purple-50"
                      : "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                  )}
                >
                  <History className="mr-2 h-4 w-4" />
                  History
                </Button>
              )}
            </div>
            <div className={cn(
              "flex flex-col w-full rounded-2xl border bg-white p-1 shadow-sm transition-shadow",
              isCircuitBreakerTripped ? "border-amber-200 bg-amber-50" : "border-slate-200 focus-within:ring-1 focus-within:ring-slate-300"
            )}>
              <Textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={1}
                className={cn(
                  "max-h-[200px] min-h-[40px] w-full resize-none border-0 bg-transparent px-3 py-2 text-sm shadow-none focus-visible:ring-0 overflow-y-auto transition-all duration-200",
                  isLoading &&
                    (isDocumentWriter
                      ? "placeholder:font-medium placeholder:text-purple-600"
                      : "placeholder:font-medium placeholder:text-blue-600"),
                  isCircuitBreakerTripped && "cursor-not-allowed text-amber-800"
                )}
                placeholder={
                  isCircuitBreakerTripped
                    ? "Safeguard paused this conversation..."
                    : isLoading
                      ? "AI is processing your message..."
                      : "Ask me anything about your business..."
                }
                disabled={isLoading || isCircuitBreakerTripped}
              />
              <div className="flex items-center justify-between px-1 pb-1 pt-1">
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800" disabled={isLoading || isCircuitBreakerTripped}>
                        <Plus className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 mb-2" side="top">
                      <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                        <Paperclip className="h-4 w-4" />
                        <span>Upload files</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                        <HardDrive className="h-4 w-4" />
                        <span>Add from Drive</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                        <Book className="h-4 w-4" />
                        <span>Notebooks</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                        <ImageIcon className="h-4 w-4" />
                        <span>Create image</span>
                        <span className="ml-auto text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm font-medium">New</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2 cursor-pointer text-slate-800 font-medium bg-slate-50"
                        onClick={() => {
                          setUseCanvasMode(true);
                          setTimeout(() => textareaRef.current?.focus(), 0);
                        }}
                      >
                        <PenSquare className="h-4 w-4 text-blue-600" />
                        <span>Canvas</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="gap-2 cursor-not-allowed opacity-50">
                        <MoreHorizontal className="h-4 w-4" />
                        <span>More tools</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {useCanvasMode && (
                    <div className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      <PenSquare className="h-3.5 w-3.5 text-slate-600" />
                      <span>Canvas</span>
                      <button 
                        onClick={(e) => { e.preventDefault(); setUseCanvasMode(false); }} 
                        className="ml-0.5 rounded-full hover:bg-slate-200 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  onClick={isGenerating ? handleStop : () => handleSendMessage()}
                  disabled={(!prompt.trim() && !isGenerating) || isCircuitBreakerTripped}
                  size="icon"
                  className={cn(
                    "h-8 w-8 rounded-full text-white transition-all duration-200",
                    isDocumentWriter
                      ? "bg-purple-600 hover:bg-purple-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  )}
                >
                  {isGenerating ? (
                    <Square className="h-4 w-4 fill-current" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Status indicator */}
            {isLoading && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <div
                  className={cn(
                    "h-2 w-2 animate-pulse rounded-full",
                    isDocumentWriter ? "bg-purple-500" : "bg-blue-500"
                  )}
                ></div>
                <span>Sending message and generating response...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Note: SimpleMessage component removed - now using StreamingMessage for all messages

// Main component with error boundary
export function RefactoredAgentChatSidebar(props: RefactoredAgentChatSidebarProps) {
  // FIX: Simplified to match original AgentChatSidebar structure
  // The parent container (dashboard-layout.tsx) handles positioning with "fixed right-0 top-16"
  // This component is just a wrapper - the header is now inside RefactoredChatSidebarContent
  return (
    <div
      className={cn(
        "flex h-full flex-col border-l border-gray-200 bg-white",
        props.fillParent ? "w-full" : "w-96",
        props.className
      )}
    >
      <ChatErrorBoundary>
        <RefactoredChatSidebarContent {...props} />
      </ChatErrorBoundary>
    </div>
  );
}
